import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useProjectNotes } from '@/hooks/useProjectNotes';
import type { ProjectNote } from '@/hooks/useProjectNotes';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Plus, Clock, User, Edit2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface ProjectNotesTabProps {
  projectId: string;
}

const noteTypeLabels: Record<string, string> = {
  note: 'Notitie',
  update: 'Update',
  milestone: 'Mijlpaal',
  issue: 'Issue',
  meeting_notes: 'Notule gesprek'
};

const noteTypeColors: Record<string, string> = {
  note: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  update: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  milestone: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  issue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  meeting_notes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
};

export const ProjectNotesTab: React.FC<ProjectNotesTabProps> = ({ projectId }) => {
  const { notes, loading, createNote, updateNote, deleteNote } = useProjectNotes(projectId);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<ProjectNote | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    body: string;
    note_type: 'note' | 'update' | 'milestone' | 'issue' | 'meeting_notes';
  }>({
    title: '',
    body: '',
    note_type: 'note'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingNote) {
        await updateNote(editingNote.id, formData);
        setEditingNote(null);
      } else {
        await createNote(formData);
        setIsCreateModalOpen(false);
      }
      setFormData({ title: '', body: '', note_type: 'note' });
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleEdit = (note: ProjectNote) => {
    setFormData({
      title: note.title,
      body: note.body,
      note_type: note.note_type
    });
    setEditingNote(note);
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setFormData({ title: '', body: '', note_type: 'note' });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-1/3"></div>
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Project Management</h3>
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe notitie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nieuwe notitie toevoegen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select
                  value={formData.note_type}
                  onValueChange={(value: 'note' | 'update' | 'milestone' | 'issue') =>
                    setFormData(prev => ({ ...prev, note_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(noteTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Titel</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Titel van de notitie"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Inhoud</label>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Beschrijving..."
                  rows={4}
                  required
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit">Toevoegen</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {notes.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">Nog geen notities toegevoegd.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Voeg notities, updates en mijlpalen toe om de voortgang bij te houden.
              </p>
            </CardContent>
          </Card>
        ) : (
          notes.map((note) => (
            <Card key={note.id} className={editingNote?.id === note.id ? 'ring-2 ring-primary' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {editingNote?.id === note.id ? (
                      <div className="space-y-2">
                        <Select
                          value={formData.note_type}
                          onValueChange={(value: 'note' | 'update' | 'milestone' | 'issue') =>
                            setFormData(prev => ({ ...prev, note_type: value }))
                          }
                        >
                          <SelectTrigger className="w-auto">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(noteTypeLabels).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="font-semibold"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge className={noteTypeColors[note.note_type]}>
                          {noteTypeLabels[note.note_type]}
                        </Badge>
                        <CardTitle className="text-base">{note.title}</CardTitle>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => editingNote?.id === note.id ? handleCancelEdit() : handleEdit(note)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Notitie verwijderen</AlertDialogTitle>
                          <AlertDialogDescription>
                            Weet je zeker dat je deze notitie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteNote(note.id)}>
                            Verwijderen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {format(new Date(note.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Door gebruiker
                  </div>
                </div>
              </CardHeader>
              <Separator />
              <CardContent className="pt-3">
                {editingNote?.id === note.id ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Textarea
                      value={formData.body}
                      onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                      rows={4}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm">Opslaan</Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit}>
                        Annuleren
                      </Button>
                    </div>
                  </form>
                ) : (
                  <p className="whitespace-pre-wrap">{note.body}</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};